<template>
    <section v-if="heroContent" class="pinned-content-hero" :class="{'multiple-content': slice.variation === 'multipleContent'}">
        <div class="content">
            <div class="main-article">
                <PrismicImage v-if="heroThumbnail" class="thumbnail" :class="{cropped: cropsThumbnail}" :field="heroThumbnail" />
                <div class="content-info">
                    <h1 class="title">{{ heroContent.data?.title }}</h1>
                    <div v-if="showsHeroTime" class="time-info">
                        <template v-if="heroReleaseDate">
                            <Calendar class="icon" />
                            <span>{{ formatDate(heroReleaseDate) }}</span>
                        </template>
                        <template v-if="heroDuration !== undefined">
                            <div class="dot" />
                            <Clock class="icon" />
                            <span>{{ heroDuration }}</span>
                        </template>
                    </div>
                    <PrismicRichText class="excerpt" :field="heroContent.data?.excerpt" />

                    <NuxtLink class="link" data-testid="pinned-content-hero-cta" :to="heroRedirect.to" :target="heroRedirect.target">
                        {{ t(ctaTranslationKey(heroContent.type)) }}
                    </NuxtLink>
                </div>
            </div>
            <div v-if="slice.variation === 'multipleContent'" class="side-column">
                <PrismicRichText :field="slice.primary.sideContentTitle" class="side-column-title" />
                <NuxtLink
                    v-for="{item, redirect} in sideContents"
                    :key="item.id"
                    :data-testid="`pinned-content-hero-side-${item.id}`"
                    :to="redirect.to"
                    :target="redirect.target"
                    class="side-content"
                >
                    <div>
                        <h3 class="side-title">{{ item.data?.title }}</h3>
                        <div v-if="showsSideDate(item)" class="time-info">
                            <Calendar class="icon" />
                            <span>{{ formatDate(item.date) }}</span>
                        </div>
                    </div>
                    <VJoyButton variant="secondary" icon="arrow-right" class="side-button" />
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {PinnedContentHeroSlice} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {NuxtLink} from '#components';
    import {useAsyncData, useFormat, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import Calendar from '~/components/icons/Calendar.vue';
    import Clock from '~/components/icons/Clock.vue';
    import type {ResourcesCenterSliceContext} from '~/types/prismicSliceContext';
    import {usePinnedContentHero} from './composables/usePinnedContentHero';
    import {usePinnedContentHeroDisplay} from './composables/usePinnedContentHeroDisplay';
    import {useResourceContentRedirect} from './composables/useResourceContentRedirect';
    import {ctaTranslationKey} from './ctaTranslationKey';
    import {hasPinnedContent} from './resolvePinnedHeroContent';
    import {fallbackSlotCount, fetchFallbackDocuments, resolveDocType} from './resolvePinnedHeroFallback';

    const {slice, context} = defineProps(
        getSliceComponentProps<PinnedContentHeroSlice, ResourcesCenterSliceContext>(['slice', 'index', 'slices', 'context']),
    );

    const {t} = useTranslation();
    const {formatDate} = useFormat();

    const route = useRoute();
    const docType = resolveDocType(context?.parentDocument, route.name);

    const {data: fallbackDocuments} = await useAsyncData(`pinned-content-hero-fallback:${docType}:${slice.variation}`, async () => {
        if (hasPinnedContent(slice) || !docType) return [];
        return fetchFallbackDocuments(docType, fallbackSlotCount(slice))();
    });

    const {hero: heroContent, sides: resolvedSideContents} = usePinnedContentHero(() => slice, fallbackDocuments);

    const {resolveContentRedirect} = useResourceContentRedirect();
    const heroRedirect = computed(() => (heroContent.value ? resolveContentRedirect(heroContent.value) : {to: ''}));
    const sideContents = computed(() => resolvedSideContents.value.map((item) => ({item, redirect: resolveContentRedirect(item)})));

    const {cropsThumbnail, heroDuration, heroReleaseDate, heroThumbnail, showsHeroTime, showsSideDate} = usePinnedContentHeroDisplay(
        heroContent,
        () => slice,
    );
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .pinned-content-hero {
        background-color: var(--joy-color-quaternary-10);
        padding: 60px 0 90px;

        @include mq.screen_sm(min) {
            padding: 110px 0 140px;
        }

        @include mq.screen_xlg(min) {
            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 70%;
                pointer-events: none;
                background-image: url('~/assets/svg/heroPills.svg');
                background-position: top left;
                background-repeat: no-repeat;
                background-size: auto 100%;
            }
        }

        .content {
            position: relative;
            z-index: 1;

            @include content-width;

            .main-article {
                display: flex;
                flex-direction: column;

                @include mq.screen_sm(min) {
                    display: grid;
                    align-items: center;
                    grid-template-columns: 1fr 1fr;
                    column-gap: var(--joy-core-spacing-9);
                }

                @include mq.screen_md(min) {
                    column-gap: var(--joy-core-spacing-12);
                }

                @include mq.screen_lg(min) {
                    column-gap: 150px;
                }
            }

            .category-pill a:not(:hover)::after {
                background-color: var(--joy-color-quaternary-10);
            }

            .thumbnail {
                width: 100%;
                object-fit: contain;
                object-position: center;
                border-radius: var(--joy-core-radius-4);

                &.cropped {
                    object-fit: cover;
                }

                @media (max-width: $mobile-breakpoint) {
                    height: auto;
                    width: 100%;
                    max-height: 350px;
                    margin-bottom: var(--joy-core-spacing-4);
                }
            }

            h1.title {
                margin-top: var(--joy-core-spacing-4);
                margin-bottom: var(--joy-core-spacing-2);
                font-size: 65px;
            }

            .time-info {
                display: flex;
                align-items: center;
                color: var(--joy-color-neutral-40);

                .dot {
                    height: 4px;
                    width: 4px;
                    background-color: var(--joy-color-neutral-60);
                    border-radius: var(--joy-core-radius-6);
                    margin: 0 var(--joy-core-spacing-4);
                }

                .icon {
                    margin-right: var(--joy-core-spacing-2);
                }
            }

            .excerpt {
                margin-top: var(--joy-core-spacing-5);

                p {
                    color: var(--joy-color-neutral-40);
                }
            }

            .tags {
                margin-top: var(--joy-core-spacing-5);
                display: flex;
                gap: var(--joy-core-spacing-1);
                flex-wrap: wrap;
            }

            .link {
                @include link;

                display: inline-block;
                margin-top: var(--joy-core-spacing-5);
            }
        }

        &.multiple-content {
            padding: var(--joy-core-spacing-9) 0 80px;

            @media (max-width: $mobile-breakpoint) {
                padding: var(--joy-core-spacing-5) 0 var(--joy-core-spacing-9);
            }

            .content {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-11);

                @include mq.screen_sm(min) {
                    display: grid;
                    grid-template-columns: 1.2fr 1fr;
                }

                @include mq.screen_md(min) {
                    column-gap: var(--joy-core-spacing-12);
                    align-items: center;
                }

                @include mq.screen_lg(min) {
                    column-gap: 150px;
                }

                .thumbnail {
                    height: 300px;
                    object-position: left;
                    object-fit: cover;
                    border-radius: var(--joy-core-radius-4);

                    @media (max-width: $mobile-breakpoint) {
                        height: auto;
                        max-height: 330px;
                    }
                }

                .main-article {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;

                    h1.title {
                        font-size: 40px;

                        @media (max-width: $mobile-breakpoint) {
                            font-size: var(--joy-font-size-secondary-200);
                            margin-bottom: var(--joy-core-spacing-5);
                        }
                    }

                    .content-info {
                        margin-top: var(--joy-core-spacing-5);
                    }

                    .link {
                        @media (max-width: $mobile-breakpoint) {
                            display: inline-block;
                        }
                    }
                }

                .side-column {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    align-self: stretch;

                    .side-column-title h2 {
                        color: var(--joy-color-primary-50);
                        font-family: var(--joy-font-family-title);
                        font-size: var(--joy-font-size-secondary-200);
                        font-weight: var(--joy-font-weight-normal);
                        padding-bottom: var(--joy-core-spacing-4);
                    }
                }

                .side-content {
                    display: flex;
                    align-items: center;
                    gap: var(--joy-core-spacing-12);
                    padding: var(--joy-core-spacing-7) 0;
                    border-bottom: solid 1px var(--joy-color-neutral-30);
                    width: 100%;
                    justify-content: space-between;

                    &:first-of-type {
                        padding-top: 0;
                    }

                    .time-info {
                        margin-top: var(--joy-core-spacing-3);
                    }

                    .side-button {
                        --button-color-secondary: var(--joy-color-primary-50);
                        --button-bg-color-secondary-hover: var(--joy-color-primary-10);

                        padding: 0 10px;

                        .joy-icon {
                            margin-right: 0;
                        }
                    }

                    &:last-child {
                        border-bottom: none;
                    }
                }
            }
        }
    }
</style>
